import React, { useState, useEffect } from "react";
import { Lock, Check } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import api from "../lib/api";

export default function ResetPassword({ token, onNavigate, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const goSignIn = () => { if (onDone) onDone(); onNavigate("login"); };

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(goSignIn, 2200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      window.history.replaceState(null, "", window.location.pathname);
    } catch (err) {
      toast.error("Could not reset password", { description: err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-ink px-5">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full bg-brand/15 blur-[150px]" />
      <div className="relative w-full max-w-md">
        <div className="glass rounded-[2rem] p-8">
          <Logo size={30} />
          {done ? (
            <div className="mt-6 text-center" data-testid="reset-success">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-sage/15 text-sage"><Check size={28} /></div>
              <h1 className="mt-5 text-2xl font-semibold text-mist">Password updated</h1>
              <p className="mt-2 text-sm text-mist-muted">Taking you to sign in…</p>
              <button onClick={goSignIn} className="btn-primary mt-6 w-full" data-testid="reset-signin-btn">Sign in now</button>
            </div>
          ) : (
            <>
              <h1 className="mt-7 text-3xl font-semibold text-mist">Set a new password</h1>
              <p className="mt-2 text-sm text-mist-muted">Choose a strong password you'll remember.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-brand">
                  <Lock size={16} className="text-mist-muted" />
                  <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password" data-testid="reset-password-input"
                    className="w-full bg-transparent text-sm text-mist outline-none placeholder:text-mist-muted/60" />
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 focus-within:border-brand">
                  <Lock size={16} className="text-mist-muted" />
                  <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm password" data-testid="reset-confirm-input"
                    className="w-full bg-transparent text-sm text-mist outline-none placeholder:text-mist-muted/60" />
                </div>
                <button type="submit" disabled={busy} data-testid="reset-submit-btn" className={`btn-primary w-full ${busy ? "opacity-60" : ""}`}>
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
